// --- FINAL, COMPLETE & CORRECTED FILE: apps/website/src/components/admin/tasks/TaskEditorForm.tsx ---

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Paperclip, MessageSquare, Link, Trash2, Plus, Star, CheckSquare } from 'lucide-react';
import clsx from 'clsx';

import {
    Button,
    IconButton,
    SelectField,
    TextField,
    TextareaField,
    MultiSelectField,
} from '@dopomogai/ui';
import { ChecklistEditor } from './ChecklistEditor';
import type { ProjectTask, TaskLink, ChecklistItem, UUID } from '@dopomogai/types';

// --- Internal Sub-component for managing links ---
const LinkManager: React.FC<{
  links: TaskLink[];
  onLinksChange: (newLinks: TaskLink[]) => void;
}> = ({ links, onLinksChange }) => {
  const { t } = useTranslation('admin_tasks');

  const handleLinkChange = (index: number, field: keyof TaskLink, value: string) => {
    const newLinks = [...(links || [])];
    newLinks[index] = { ...newLinks[index], [field]: value };
    onLinksChange(newLinks);
  };

  const addLink = () => onLinksChange([...(links || []), { text: '', url: '' }]);
  const removeLink = (index: number) => onLinksChange((links || []).filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      {(links || []).map((link, index) => (
        <div key={index} className="flex items-center gap-3 p-3 bg-white border-2 border-black rounded-lg">
          <Link className="flex-shrink-0 text-slate-400" size={18} />
          <TextField
            name={`link-text-${index}`}
            value={link.text}
            onChange={(e) => handleLinkChange(index, 'text', e.target.value)}
            containerClassName="flex-grow"
            placeholder={t('editor.links.textPlaceholder')}
          />
          <TextField
            name={`link-url-${index}`}
            value={link.url}
            onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
            containerClassName="flex-grow"
            placeholder={t('editor.links.urlPlaceholder')}
          />
          <IconButton
            icon={<Trash2 size={16} />}
            onClick={() => removeLink(index)}
            variant="danger"
            tooltip={t('editor.links.removeTooltip')}
          />
        </div>
      ))}
      <Button textKey="editor.links.addButton" variant="secondary" icon={<Plus size={16} />} onClick={addLink} />
    </div>
  );
};


interface TaskEditorFormProps {
  taskData: ProjectTask;
  onDataChange: (data: Partial<ProjectTask>) => void;
  availableUsers?: { value: UUID; label: string }[];
}

/**
 * A workspace-style form for viewing and editing a Project Task.
 */
export const TaskEditorForm: React.FC<TaskEditorFormProps> = ({
  taskData,
  onDataChange,
  availableUsers = [],
}) => {
  const { t } = useTranslation('admin_tasks');

  const statusOptions = [
    { value: 'todo', label: 'Todo' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'done', label: 'Done' },
    { value: 'blocked', label: 'Blocked' },
    { value: 'archived', label: 'Archived' },
  ];

  const typeOptions = [
    { value: 'feature', label: 'Feature' },
    { value: 'bugfix', label: 'Bugfix' },
    { value: 'chore', label: 'Chore' },
    { value: 'docs', label: 'Docs' },
    { value: 'research', label: 'Research' },
  ];

  return (
    <div className="space-y-10">
      {/* --- Section 1: Primary Content (Title & Description) --- */}
      <section className="relative">
        {/* Placeholder text is now handled by the TextareaField's placeholder prop */}
        <TextareaField
            name="task-title"
            value={taskData.title}
            onChange={(e) => onDataChange({ title: e.target.value })}
            placeholder={t('editor.titlePlaceholder')}
            variant="ghost"
            autoGrow
            textareaClassName="text-3xl font-extrabold text-slate-900 !min-h-[44px] resize-none leading-tight"
            rows={1}
        />
        <TextareaField
            name="task-description"
            value={taskData.description || ''}
            onChange={(e) => onDataChange({ description: e.target.value })}
            placeholder={t('editor.descriptionPlaceholder')}
            variant="ghost"
            autoGrow
            containerClassName="mt-2"
            textareaClassName="text-base text-slate-700 leading-relaxed"
            rows={3}
        />
      </section>

      {/* --- Section 2: Details Panel --- */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Settings size={14} />
            {t('editor.details.sectionTitle')}
        </h3>
        <div className="p-5 bg-slate-50 border-2 border-dashed rounded-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <SelectField
              name="task-status"
              label={t('editor.details.statusLabel')}
              value={taskData.status}
              onChange={val => onDataChange({ status: val as ProjectTask['status'] })}
              options={statusOptions}
            />
             <SelectField
              name="task-type"
              label={t('editor.details.typeLabel')}
              value={taskData.task_type}
              onChange={val => onDataChange({ task_type: val as ProjectTask['task_type'] })}
              options={typeOptions}
            />
            <MultiSelectField
                name="task-assignees"
                label={t('editor.details.assigneesLabel')}
                options={availableUsers}
                value={taskData.assignee_ids || []}
                onChange={ids => onDataChange({ assignee_ids: ids as string[] })}
                placeholder={t('editor.details.assigneesPlaceholder')}
            />
            <TextField
              name="task-points"
              label={t('editor.details.pointsLabel')}
              type="number"
              value={String(taskData.points || '')}
              onChange={(e) => onDataChange({ points: Number(e.target.value) || undefined })}
              leadingIcon={<Star size={18} className="text-slate-400" />}
            />
          </div>
        </div>
      </section>

      {/* --- Section 3: Checklist --- */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <CheckSquare size={14} />
            {t('editor.checklist.sectionTitle')}
        </h3>
        <div className="p-5 bg-slate-50 border-2 border-dashed rounded-2xl">
           <ChecklistEditor
              items={(taskData as any).checklist || []}
              onItemsChange={(items) => onDataChange({ checklist: items } as any)}
           />
        </div>
      </section>

      {/* --- Section 4: Attachments & Links --- */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Paperclip size={14} />
            {t('editor.links.sectionTitle')}
        </h3>
        <div className="p-5 bg-slate-50 border-2 border-dashed rounded-2xl space-y-4">
          <LinkManager links={taskData.links || []} onLinksChange={(links) => onDataChange({ links })}/>
          <div className="pt-3 border-t border-slate-200">
             <Button textKey="editor.links.attachFileButton" variant="ghost" icon={<Paperclip size={16}/>} disabled />
          </div>
        </div>
      </section>

      {/* --- Section 5: Activity / Comments --- */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <MessageSquare size={14} />
            {t('editor.activity.sectionTitle')}
        </h3>
        <div className="bg-white p-4 border-2 border-black rounded-xl">
            <TextareaField
                name="new-comment"
                placeholder={t('editor.activity.commentPlaceholder')}
                rows={3}
                value=""
                onChange={() => {}} // Placeholder
                containerClassName="!mb-0"
            />
            <div className="mt-3 flex justify-between items-center">
                <div className="flex items-center gap-1">
                    <IconButton icon={<Paperclip />} tooltip={t('editor.activity.attachFileTooltip')} disabled />
                </div>
                <Button textKey="editor.activity.postCommentButton" disabled />
            </div>
        </div>
      </section>
    </div>
  );
};